import 'twin.macro';
import { v4 as uuid } from 'uuid';
import React, { useEffect, useRef, useState } from 'react';
import { Csv, Mapping } from './csv';
import { StepLayout } from './components/StepLayout';
import { Header } from './components/Header';
import { DownloadCsv } from './DownloadCsv';
import { Upload } from './Upload';
import { FieldMappings } from './FieldMappings';
import { Finalize } from './Finalize';
import useImportEleves from '@teetsh/app/src/queries/eleves/useImportEleves';
import { Success } from './Success';
import { guessMapping } from './guessMapping';
import useEleves from '@teetsh/app/src/queries/eleves/useEleves';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import { useSchoolGoTo } from '@teetsh/app/src/containers/App/useSchoolGoTo';
import { useTranslation } from 'react-i18next';
import Button from 'components/Buttons/Button';
import CelebrationIcon from 'containers/Dashboard/OnboardingCheckList/Celebration.svg';
import { IllustratedPage } from '@teetsh/app/src/components/IllustratedPage';
import { Infobox } from 'components/Callout/Infobox';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import ButtonExternal from 'components/Buttons/ExternalLink';
import { tutoUrls } from 'containers/tutoUrls';
import { useCountryConfig } from '@teetsh/app/src/queries/user/useCountryConfig';

type Step = 'download' | 'upload' | 'map' | 'finalize';

interface Props {
  onBackButtonClick?: () => void;
  onSuccess?: () => void;
  successCta?: string;
}

export function FileImport(props: Props) {
  const { isImportFileFirstStepEnabled } = useCountryConfig();
  const stepOffset = isImportFileFirstStepEnabled ? 0 : -1;
  const [step, setStep] = useState<Step>(
    isImportFileFirstStepEnabled ? 'download' : 'upload',
  );
  const { importEleves, isImporting, isSuccess } = useImportEleves();
  const { t } = useTranslation(['eleve', 'common']);
  const [csv, setCsv] = useState<Csv | null>(null);
  const myRef = useRef(null);
  const goTo = useSchoolGoTo();
  const [mappings, setMappings] = useState<Mapping[] | null>(null);
  useEffect(() => {
    setMappings(guessMapping(csv?.headers || []));
  }, [csv]);
  const { data, isLoading } = useEleves();
  if (isLoading || !data) {
    return <SpinnerComponent />;
  }
  const goToElevesPage = () => goTo('eleves');

  if (isSuccess) {
    return (
      <IllustratedPage
        illustration={
          <img src={CelebrationIcon} alt="" tw="w-[532px] h-[317px]" />
        }
        title={t('eleve:import.successMessage')}
      >
        <Button
          intent="primary"
          large
          onClick={props.onSuccess || goToElevesPage}
        >
          {t('eleve:import.seeStudentsList')}
        </Button>
      </IllustratedPage>
    );
  }

  return (
    <div tw="flex flex-col items-center pb-24 px-4">
      <Header
        onBackButtonClick={props.onBackButtonClick || goToElevesPage}
        title={t('eleve:import.importFile')}
      />

      <div tw="w-full max-w-[60rem] flex flex-col gap-6 mb-24" ref={myRef}>
        <Success
          number={csv?.rows.length}
          show={isSuccess}
          successCta={props.successCta}
          onSuccess={props.onSuccess || goToElevesPage}
        />
        {!isSuccess && (
          <>
            <Infobox
              intent="primary_intense"
              layout="inline"
              title={t('eleve:needHelp')}
              icon={QuestionMarkCircleIcon}
              actions={
                <ButtonExternal
                  small
                  intent="primary"
                  outlined
                  href={tutoUrls.eleveDocumentationAddEleves}
                >
                  {t('eleve:openDocumentation')}
                </ButtonExternal>
              }
            >
              {t('eleve:helpDescription')}
            </Infobox>

            <div tw="flex flex-col gap-4">
              {isImportFileFirstStepEnabled && (
                <StepLayout
                  step={1}
                  title={t('eleve:import.downloadCsvFile')}
                  description={t('eleve:import.downloadCsvDescription')}
                  isOpen={step === 'download'}
                  isSuccess={step !== 'download'}
                  resetButton={
                    step !== 'download'
                      ? {
                          onClick: () => {
                            setCsv(null);
                            setStep('download');
                          },
                          label: t('common:buttons.see'),
                        }
                      : undefined
                  }
                >
                  <DownloadCsv onNext={() => setStep('upload')} />
                </StepLayout>
              )}

              <StepLayout
                step={2 + stepOffset}
                title={t('eleve:import.downloadYourFile')}
                description={t('eleve:import.selectCsvFile')}
                isOpen={step === 'upload'}
                isSuccess={step === 'map' || step === 'finalize'}
                resetButton={
                  step === 'map' || step === 'finalize'
                    ? {
                        onClick: () => {
                          setMappings(null);
                          setStep('upload');
                        },
                        label: t('common:buttons.modify'),
                      }
                    : undefined
                }
              >
                <Upload
                  setCsv={c => {
                    setCsv(c);
                    setStep('map');
                  }}
                />
              </StepLayout>

              <StepLayout
                step={3 + stepOffset}
                title={t('eleve:import.matchFileFields')}
                description={t('eleve:import.selectOnTheRightColumnsFields')}
                isOpen={step === 'map'}
                isSuccess={step === 'finalize'}
                resetButton={
                  step === 'finalize'
                    ? {
                        onClick: () => setStep('map'),
                        label: t('common:buttons.modify'),
                      }
                    : undefined
                }
              >
                <FieldMappings
                  csv={csv}
                  mappings={mappings}
                  setMappings={m => {
                    setMappings(m);
                    setStep('finalize');
                  }}
                  onResetToUpload={() => {
                    setCsv(null);
                    setMappings(null);
                    setStep('upload');
                  }}
                />
              </StepLayout>

              <StepLayout
                step={4 + stepOffset}
                title={t('eleve:import.finalization')}
                isOpen={step === 'finalize'}
                isSuccess={false}
              >
                <Finalize
                  isUploading={isImporting}
                  mappings={mappings}
                  rows={csv?.rows || []}
                  eleves={data || []}
                  onConfirm={detailMapping =>
                    importEleves({
                      classrooms: detailMapping.classrooms.map(cl => ({
                        ...cl,
                        id: cl.id === 'create' ? uuid() : cl.id,
                        eleves: cl.eleves.map(ev => ({
                          ...ev,
                          id: ev.id === 'create' ? uuid() : ev.id,
                        })),
                      })),
                    })
                  }
                />
              </StepLayout>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
